/**
 * First sign-in: pick your region.
 *
 * The one choice a person makes for themselves. It puts them in that region's
 * user role, which sees nothing until an admin grants areas — so picking the
 * wrong region costs nothing but a conversation, and only a global admin can
 * move them afterwards. That is said plainly below, because it is the one
 * thing here that cannot be undone from this screen.
 */

import { useState } from 'react'
import { useRegisterRegion, useSchema } from '../api/hooks'
import { ErrorBanner } from '../components/feedback'

export function RegionPicker({ onSignOut }: { onSignOut: () => void }) {
  const { data } = useSchema()
  const register = useRegisterRegion()
  const [choice, setChoice] = useState('')

  const regions = data?.regions ?? []

  return (
    <div className="signin">
      <form
        className="card signin__card"
        onSubmit={(event) => {
          event.preventDefault()
          if (choice) register.mutate(choice)
        }}
      >
        <h1 className="signin__title">Which region do you work in?</h1>
        <p className="signin__body">
          Welcome{data?.user.display_name ? `, ${data.user.display_name}` : ''}. Pick the
          region whose email automation you support. An admin for that region then gives
          you access to the screens you need.
        </p>

        <fieldset className="region-picker">
          <legend className="visually-hidden">Region</legend>
          {regions.map((region) => (
            <label key={region.key} className="region-picker__option">
              <input
                type="radio"
                name="region"
                value={region.key}
                checked={choice === region.key}
                onChange={() => setChoice(region.key)}
                disabled={register.isPending}
              />
              <span>{region.label}</span>
            </label>
          ))}
        </fieldset>

        <p className="field__help">
          Only a global admin can move you to a different region later.
        </p>

        <ErrorBanner error={register.error} context="Your region was not saved." />

        <div className="region-picker__actions">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={!choice || register.isPending}
          >
            {register.isPending ? 'Saving…' : 'Continue'}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </form>
    </div>
  )
}
